import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { pricingApi, listChannelOverrideChanges, CHANNEL_LABEL, type Channel, type ChangeStatus } from "@/services/pricing";
import { PageShell, Pill, EmptyState, LoadingState } from "@/shared/ui/PageHeader";
import { useCan } from "@/auth/permissions";
import { Card } from "@/shared/ui/Card";

/** 10-10 14:20 */
const stamp = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** CHOVR-1 · 改价记录里的「单产品系数」类型（谁、什么时候、哪个产品、改了什么、原因）。 */
function ProductOverrideLog() {
  const canView = useCan("pricing.cost.view");
  const q = useQuery({ queryKey: ["pricing", "channel-override-changes"], enabled: canView, queryFn: () => listChannelOverrideChanges(100) });
  if (!canView) return null;
  const list = Array.isArray(q.data) ? q.data : [];
  return (
    <Card title="单产品系数记录" action={<span className="text-[11px] text-slate-400">售价总览 / 产品详情里「调整系数」保存一次记一条 · 含原因</span>}>
      {q.isLoading ? (
        <LoadingState />
      ) : q.isError ? (
        <div className="flex flex-col items-center gap-2 py-8 text-[13px] text-slate-500"><span>单产品系数记录加载失败</span><button onClick={() => q.refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button></div>
      ) : list.length === 0 ? (
        <EmptyState message="暂无单产品系数调整记录" />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="override-log">
          <table className="w-full min-w-[820px] text-[13px] tabular-nums">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3 text-left">时间</th><th className="px-4 py-3 text-left">操作人</th><th className="px-4 py-3 text-left">类型</th><th className="px-4 py-3 text-left">产品</th><th className="px-4 py-3 text-left">改动</th><th className="px-4 py-3 text-left">原因</th></tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 align-top">
                  <td className="whitespace-nowrap px-4 py-3 text-slate-600">{stamp(r.at)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-700">{r.actor_name}</td>
                  <td className="whitespace-nowrap px-4 py-3"><span className="inline-flex h-5 items-center whitespace-nowrap rounded-full bg-violet-50 px-2 text-[11px] font-semibold text-violet-700">{r.type_label}</span></td>
                  <td className="px-4 py-3 text-slate-700">{[r.product_code, r.product_name].filter(Boolean).join(" ")}</td>
                  <td className="px-4 py-3 text-slate-700">{(r.changes ?? []).join("；")}</td>
                  <td className="max-w-[260px] px-4 py-3 text-slate-500">{r.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export function PricingChangeLogPage() {
  const [filter, setFilter] = useState<ChangeStatus | "">("pending");
  const qc = useQueryClient();
  const isOwner = useCan("pricing.change_requests.approve");

  const changes = useQuery({
    queryKey: ["pricing", "changes", filter],
    queryFn: () => pricingApi.listChanges(filter || undefined),
  });

  const decide = useMutation({
    mutationFn: ({ rid, approve }: { rid: string; approve: boolean }) =>
      pricingApi.decideChange(rid, approve),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing"] }),
    onError: (e) => alert((e as Error).message || "审批失败"),
  });

  return (
    <PageShell
      title="价格变更日志"
      subtitle="D3B 老板审批队列 · 提交 → pending → 批准 / 拒绝 → 生效"
      actions={
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as ChangeStatus | "")}
          className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-[13px]"
        >
          <option value="">全部状态</option>
          <option value="pending">待审</option>
          <option value="approved">已批准</option>
          <option value="rejected">已拒绝</option>
        </select>
      }
    >
      {changes.isLoading ? (
        <LoadingState />
      ) : changes.isError ? (
        <div className="flex flex-col items-center gap-2 py-16 text-[13px] text-slate-500"><span>变更日志加载失败</span><button onClick={() => changes.refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button></div>
      ) : (changes.data ?? []).length === 0 ? (
        <EmptyState message="暂无变更记录" />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">目标</th>
                <th className="px-4 py-3 text-right">变更</th>
                <th className="px-4 py-3 text-left">理由</th>
                <th className="px-4 py-3 text-left">提交人</th>
                <th className="px-4 py-3 text-left">状态</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {(changes.data ?? []).map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-medium">
                    {c.target_kind === "channel_markup"
                      ? CHANNEL_LABEL[c.target_ref as Channel]
                      : `专属价 ${c.target_ref.slice(0, 6)}`}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-slate-500">{c.current_pct}%</span>
                    <span className="mx-1.5">→</span>
                    <span className="font-bold text-slate-900">{c.proposed_pct}%</span>
                    <div
                      className={`text-[11px] ${
                        c.delta_pct > 0 ? "text-emerald-600" : c.delta_pct < 0 ? "text-rose-600" : "text-slate-500"
                      }`}
                    >
                      {c.delta_pct > 0 ? "+" : ""}{c.delta_pct.toFixed(1)}%
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate">{c.reason}</td>
                  <td className="px-4 py-3 text-[11px] text-slate-500">{c.submitted_by.slice(0, 8)}</td>
                  <td className="px-4 py-3">
                    <Pill
                      tone={
                        c.status === "approved"
                          ? "success"
                          : c.status === "rejected"
                            ? "danger"
                            : "warning"
                      }
                    >
                      {c.status === "pending" ? "待老板审" : c.status === "approved" ? "已批准" : "已拒绝"}
                    </Pill>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {c.status === "pending" && isOwner ? (
                      <div className="flex justify-end gap-1.5">
                        <button
                          type="button"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ rid: c.id, approve: true })}
                          className="flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white disabled:opacity-50"
                        >
                          <Check className="h-3 w-3" /> {decide.isPending ? "处理中…" : "批准"}
                        </button>
                        <button
                          type="button"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ rid: c.id, approve: false })}
                          className="flex items-center gap-1 rounded-md bg-rose-600 px-2.5 py-1 text-[11px] font-semibold text-white disabled:opacity-50"
                        >
                          <X className="h-3 w-3" /> 拒绝
                        </button>
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ProductOverrideLog />
    </PageShell>
  );
}
